import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { jsonApi } from '@/lib/jsonApi';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const Stat = ({ label, value, testId }) => (
  <div className="min-w-[96px]" data-testid={testId}>
    <p className="text-xl font-bold text-[#0F172A] font-['Montserrat'] leading-none">{value ?? 0}</p>
    <p className="text-[11px] text-[#64748B] mt-1">{label}</p>
  </div>
);

function JobResult({ job }) {
  const c = job.counts || {};
  const running = job.status === 'running';
  const pct = job.total ? Math.round((job.processed / job.total) * 100) : 100;
  return (
    <div className="mt-5 pt-5 border-t border-white/60" data-testid="backfill-result" data-status={job.status}>
      <p className="text-xs font-semibold text-[#334155] flex items-center gap-1.5 mb-3">
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : job.status === 'done' ? <CheckCircle2 className="w-3.5 h-3.5 text-[#1F684A]" /> : <AlertCircle className="w-3.5 h-3.5 text-[#B45309]" />}
        {running ? `Tagging… ${job.processed} of ${job.total}` : job.status === 'done' ? 'Last run finished' : 'Last run was interrupted — run again to continue'}
      </p>
      {running && <Progress value={pct} className="h-2 mb-4" data-testid="backfill-progress" />}
      <div className="flex flex-wrap gap-x-8 gap-y-4">
        <Stat label="Responses processed" value={job.processed} testId="backfill-processed" />
        <Stat label="Responses tagged" value={c.tagged} testId="backfill-tagged" />
        <Stat label="Tags added" value={job.tags_added} testId="backfill-tags-added" />
        <Stat label="No feedback text" value={c.no_feedback} testId="backfill-no-feedback" />
        <Stat label="No matching factor" value={c.no_match} testId="backfill-no-match" />
        {(c.failed > 0 || c.no_factors > 0) && <Stat label="Not processed" value={(c.failed || 0) + (c.no_factors || 0)} testId="backfill-failed" />}
      </div>
      {!running && job.remaining_after > 0 && <p className="text-xs text-[#B45309] mt-3" data-testid="backfill-remaining">{job.remaining_after} more responses remain — run again to continue.</p>}
    </div>
  );
}

export default function BackfillCard({ canEdit, hasFactors }) {
  const [state, setState] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const load = useCallback(() => jsonApi('/taxonomy/backfill').then(setState).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = state?.job?.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, load]);
  const start = async () => {
    try { const d = await jsonApi('/taxonomy/backfill', 'POST'); setState((s) => ({ ...s, job: d.job })); toast.success('Tagging past responses started'); }
    catch (e) { toast.error(e.message); }
  };
  if (!state) return null;
  const eligible = state.eligible;
  return (
    <section className="rounded-2xl border border-[#D9E2F0] bg-[#F3F6FB] p-5 sm:p-6" data-testid="backfill-card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="max-w-2xl">
          <p className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#1A3E6F]" /> Tag past responses</p>
          <p className="text-sm text-[#475569] mt-1" data-testid="backfill-eligible">
            {eligible ? `${eligible} past response${eligible === 1 ? '' : 's'} haven’t been reviewed by AI yet.` : 'Every past response has been reviewed by AI.'}
            {' '}AI reads only written and transcribed voice feedback, never scores or stars.
            {!hasFactors && eligible > 0 && <span className="block text-xs text-[#B45309] mt-1" data-testid="backfill-needs-factors">Add at least one active factor first.</span>}
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setConfirm(true)} disabled={running || !eligible || !hasFactors} className="bg-[#1A3E6F] hover:bg-[#15325A] shrink-0" data-testid="backfill-start-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}{running ? 'Tagging…' : 'Tag past responses'}
          </Button>
        )}
      </div>
      {state.job && <JobResult job={state.job} />}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="backfill-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tag {Math.min(eligible, state.cap)} past responses?</AlertDialogTitle>
            <AlertDialogDescription>
              AI will tag untagged responses using each survey’s active factors. Existing tags and anything your team added, removed or confirmed stay exactly as they are.
              {eligible > state.cap ? ` Up to ${state.cap} responses are processed per run.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backfill-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={start} className="bg-[#1A3E6F] hover:bg-[#15325A]" data-testid="backfill-confirm-btn">Start tagging</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
